import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { green } from "@mui/material/colors";
import LockIcon from "@mui/icons-material/Lock";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ApplicationType,
  entity_id,
  locationGeo,
  Platform,
  tapay_callbackurl,
} from "../Utils/Const";
import { baseapi, frontbaseapi, paymentbaseapi } from "../Environmentalurl";
import DashboardHeader from "../Dashboard/DashboardHeader";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";
import { setLoginMeta } from "../Slice/authSlice";
import { useDispatch, useSelector } from "react-redux";

const Loadusingccdc = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();

  const [cardInfo, setCardInfo] = useState(null);
  const [binFetched, setBinFetched] = useState(false);
  const [cardType, setCardType] = useState("credit");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [nameOnCard, setNameOnCard] = useState("");
  const [errors, setErrors] = useState({});
  const [user, setUser] = useState({});
  const [isPaying, setIsPaying] = useState(false);

  const userData = useSelector((state) => state.auth.user);

  // ✅ amount AND orderId both come from Redux — set by WalletLoadMoney
  // BEFORE navigating here. tapgcredit (funding-limit check) already ran
  // there once. We must NOT call it again here — just reuse the same
  // orderId for the TAPay form submission below.
  const loginMeta = useSelector((state) => state.auth.loginMeta);
  const amount = loginMeta?.amount;
  const orderId = loginMeta?.orderId;

  const ip = useIPAddress();
  const navigate = useNavigate();
  const location = useLocation();
  const expiryRef = useRef(null);

  const CARD_LOGOS = {
    VISA: "/Assets/cards/32px-Visa_Inc._logo.svg.png",
    MAST: "/Assets/cards/MasterCard_Logo.svg.png",
    RUPAY: "/Assets/cards/Rupay-Logo.png",
    AMEX: "/Assets/cards/American_Express_logo_(2018).svg.png",
    DISCOVER: "/Assets/cards/Discover-logo.png",
    DINERS: "/Assets/cards/diners-club.png",
    JCB: "/Assets/cards/JCB_logo.svg.png",
  };

  // ─── Navigation ───────────────────────────────────────────────────────────
  const handlenavigate = () => {
    localStorage.removeItem("loginMeta");
    dispatch(setLoginMeta(null));
    navigate("/dashboard/loadmoney");
  };

  useEffect(() => {
    if (!amount || !orderId) {
      navigate("/dashboard/loadmoney", { replace: true });
    }
  }, [amount, orderId, navigate]);

  useEffect(() => {
    if (!orderId) return; // the other guard effect already handles missing orderId

    let attemptFlag = null;
    try {
      const raw = sessionStorage.getItem("tapayAttempt");
      attemptFlag = raw ? JSON.parse(raw) : null;
    } catch {
      attemptFlag = null;
    }

    if (attemptFlag?.orderId === orderId) {
      sessionStorage.removeItem("tapayAttempt");
      dispatch(setLoginMeta(null));
      navigate("/dashboard/loadmoney", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Balance Enquiry ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchBalanceEnquiry = async () => {
      try {
        const payload = {
          ApplicationType,
          EntityID: entity_id,
          GeoLocation: locationGeo,
          MachineID: ip,
          Mobilenumber: userData?.MobileNumber,
          Platform,
          ReqType: "Jan_Aadhaar_eWallet_Balance_Enquiry",
          SessionID: userData?.SessionID,
        };
        const response = await api.post(
          `${baseapi}/JanAadhaareWalletBalanceEnquiry`,
          payload,
        );
        if (response.data.Response_Code === "1000") {
          setUser((prev) => ({
            ...prev,
            Balance: response.data.Balance,
            LedgerBalance: response.data.LedgerBalance,
          }));
        }
      } catch (error) {
        console.error("Balance Enquiry API error:", error);
      }
    };
    fetchBalanceEnquiry();
  }, [location.search]);

  // ─── BIN Lookup ───────────────────────────────────────────────────────────
  const fetchCardInfo = async (bin) => {
    try {
      const payload = {
        CardNumber: bin,
        EntityID: entity_id,
        SessionID: userData?.SessionID,
        Mobilenumber: userData?.MobileNumber,
        MachineID: ip,
        GeoLocation: locationGeo,
        ReqType: "TA_Get_PayUCardInfo",
        Platform,
      };
      const response = await api.post(`${baseapi}/tagetpayucardinfo`, payload);
      if (response.data?.Response_Code === "2980") {
        setCardInfo(response.data.Info);
        setCardType(
          response.data.Info.cardCategory === "CC" ? "credit" : "debit",
        );
      }
    } catch (error) {
      console.error("BIN lookup failed", error);
      setCardInfo(null);
    }
  };

  // ─── Submit Payment ───────────────────────────────────────────────────────
  // tapgcredit is NOT called here anymore. WalletLoadMoney already called it
  // once and stored the resulting orderId in Redux (loginMeta.orderId).
  // This handler only validates the card form and submits it to the TAPay
  // gateway using that SAME orderId, so the gateway's order matches the
  // funding-limit check that already ran.
  const handlePay = async () => {
    // ── 1. Validate form ──
    let tempErrors = {};
    if (!userData?.MobileNumber)
      tempErrors.mobile = "Mobile number not found. Please log in again.";
    if (!amount) tempErrors.amount = "Amount is required";
    else if (Number(amount) < 10)
      tempErrors.amount = "Amount must be at least Rs. 10";
    if (!orderId)
      tempErrors.general =
        "Session expired. Please start again from Load Money.";
    if (!cardNumber || cardNumber.replace(/\s+/g, "").length < 16)
      tempErrors.cardNumber = "Enter a valid 16-digit card number";
    if (!expiry || !/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry))
      tempErrors.expiry = "Enter valid expiry (MM/YY)";
    if (!cvv || cvv.length < 3) tempErrors.cvv = "Enter a valid 3-digit CVV";
    if (!nameOnCard.trim()) tempErrors.nameOnCard = "Name on card is required";

    setErrors(tempErrors);
    if (Object.keys(tempErrors).length > 0) return;

    setIsPaying(true);

    try {
      // ── 2. Submit payment form to TAPay gateway using the EXISTING orderId ──
      const formattedExpiry = expiry.replace(/(\d{2})\/(\d{2})$/, "$1/20$2");

      const params = {
        mobileNum: userData?.MobileNumber,
        amount: amount,
        cardType: cardType,
        cardNumber: cardNumber.replace(/\s+/g, ""),
        expiry_date: formattedExpiry,
        cvv: cvv,
        card_holder_name: nameOnCard,
        returnUrl: `${frontbaseapi}/dashboard`,
        orderID: orderId, 
        email: userData?.Poiemail,
        address1: userData?.Poastate,
        address2: userData?.Poavtc,
        city: userData?.Poadist,
        responseurl:tapay_callbackurl
      };

      console.log("=== tapay_cc_submit FORM SUBMISSION ===");
      console.log("URL:", `${paymentbaseapi}/seemless/enc/tapay_cc_submit`);
      console.log("OrderID (from WalletLoadMoney's tapgcredit call):", orderId);
      console.log("Payload:", params);

      // ✅ Mark this orderId as "sent to gateway" BEFORE navigating away.
      // This is what the mount-time guard effect checks on return — it
      // works regardless of how the user gets back here (back button,
      // gesture, bfcache, fresh reload), unlike history-API tricks which
      // depend on the gateway's own navigation behavior.
      try {
        sessionStorage.setItem(
          "tapayAttempt",
          JSON.stringify({ orderId, at: Date.now() }),
        );
      } catch {
        // sessionStorage unavailable (e.g. private mode edge case) —
        // non-fatal, the other guard (missing orderId) still helps.
      }

      const form = document.createElement("form");
      form.method = "GET";
      form.action = `${paymentbaseapi}/seemless/enc/tapay_cc_submit`;

      Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value ?? "";
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error("Payment error:", error);
      setIsPaying(false);
      setErrors((prev) => ({
        ...prev,
        general: "Something went wrong. Please try again.",
      }));
    }
  };

  // ─── Styling ──────────────────────────────────────────────────────────────
  const textColor = isDark ? "#ffffff" : "#0e1b2c";
  const inputSx = { height: 45, borderRadius: "8px", fontSize: 14 };
  const inputPadding = { style: { padding: "10px 12px" } };

  return (
    <>
      <Box px={7} bgcolor={isDark ? "#081028" : ""}>
        <DashboardHeader />
      </Box>

      <Box sx={{ maxWidth: "1100px", mx: "auto", mt: 4 }}>
        <Box
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            border: isDark
              ? "1px solid rgba(90, 126, 173, 0.49)"
              : "1px solid rgba(0,0,0,0.2)",
          }}
        >
          <Grid
            container
            display="flex"
            justifyContent="center"
            bgcolor={isDark ? "#0B1C30" : "#f5f5f5"}
            p={5}
          >
            {/* ── LEFT ── */}
            <Grid size={{ sm: 6, md: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 3,
                  width: "fit-content",
                  cursor: "pointer",
                  transition: "0.2s ease",
                }}
                onClick={handlenavigate}
              >
                <ArrowBackIosNewOutlinedIcon
                  sx={{ fontSize: 18, color: textColor }}
                />
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: textColor,
                    letterSpacing: "0.3px",
                  }}
                >
                  Back
                </Typography>
              </Box>

              <Typography variant="h6" fontWeight={600} mb={1}>
                Wallet Balance : ₹{user?.Balance}
              </Typography>

              <TextField
                fullWidth
                label="Enter Amount in rupees"
                variant="standard"
                type="number"
                value={amount}
                InputProps={{
                  startAdornment: (
                    <CurrencyRupeeIcon fontSize="small" sx={{ mr: 1 }} />
                  ),
                }}
              />

              <Card
                variant="outlined"
                sx={{
                  backgroundColor: isDark ? "#12223b" : "#f9f9f9",
                  mb: 3,
                  maxWidth: 500,
                  px: 3,
                  mt: 5,
                }}
              >
                <CardContent>
                  <Typography
                    variant="body2"
                    textAlign="left"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Note:
                  </Typography>
                  <Typography variant="body2" mb={0.5}>
                    1. Minimum Load Money amount is Rs.10/-.
                  </Typography>
                  <Typography variant="body2" mb={0.5}>
                    2. Do not use other wallets or virtual cards.
                  </Typography>
                  <Typography variant="body2">
                    3. If amount is debited and wallet is not credited contact
                    customer care or wait for 2 working days before complaining
                    to the bank.
                  </Typography>
                </CardContent>
              </Card>

              <Typography
                variant="body2"
                sx={{ color: green[700], mt: 1, cursor: "pointer" }}
              >
                Know Your Wallet Limits ↓
              </Typography>
            </Grid>

            {/* ── RIGHT ── */}
            <Grid size={{ sm: 6, md: 6 }}>
              <Box sx={{ maxWidth: 400, mx: "auto" }}>
                <Typography variant="h6" mb={2} fontWeight={600}>
                  Credit / Debit Card
                </Typography>

                {errors.general && (
                  <Typography variant="body2" color="error" mb={1}>
                    {errors.general}
                  </Typography>
                )}

                {/* Card Number */}
                <Typography
                  textAlign="left"
                  color={isDark ? "white" : "#00050b"}
                  sx={{ mb: 0.5 }}
                >
                  Card Number
                </Typography>
                <TextField
                  fullWidth
                  placeholder="1234 5678 9012 3456"
                  margin="dense"
                  value={cardNumber}
                  autoComplete="off"
                  error={Boolean(errors.cardNumber)}
                  helperText={errors.cardNumber}
                  onChange={(e) => {
                    let digits = e.target.value.replace(/\D/g, "").slice(0, 16);
                    if (digits.length >= 6 && !binFetched) {
                      fetchCardInfo(digits.slice(0, 6));
                      setBinFetched(true);
                    }
                    if (digits.length < 6 && binFetched) {
                      setBinFetched(false);
                      setCardInfo(null);
                    }
                    setCardNumber(digits.replace(/(.{4})/g, "$1 ").trim());
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCardIcon />
                      </InputAdornment>
                    ),
                    endAdornment:
                      cardInfo?.cardType && CARD_LOGOS[cardInfo.cardType] ? (
                        <InputAdornment position="end">
                          <img
                            src={CARD_LOGOS[cardInfo.cardType]}
                            alt={cardInfo.cardType}
                            style={{
                              width: 38,
                              height: "auto",
                              objectFit: "contain",
                            }}
                          />
                        </InputAdornment>
                      ) : null,
                    sx: inputSx,
                  }}
                />

                {/* Expiry + CVV */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ width: "50%" }}>
                    <Typography
                      textAlign="left"
                      color={isDark ? "white" : "#00050b"}
                      sx={{ mb: 0.5 }}
                    >
                      Expiry Date
                    </Typography>
                    <TextField
                      placeholder="MM/YY"
                      fullWidth
                      margin="dense"
                      sx={{ mb: 1 }}
                      value={expiry}
                      autoComplete="off"
                      onChange={(e) => {
                        let val = e.target.value.replace(/\D/g, "");
                        if (val.length > 2)
                          val = val.slice(0, 2) + "/" + val.slice(2, 4);
                        setExpiry(val);
                      }}
                      error={Boolean(errors.expiry)}
                      helperText={errors.expiry}
                      inputRef={expiryRef}
                      InputProps={{ sx: inputSx }}
                      inputProps={inputPadding}
                    />
                  </Box>
                  <Box sx={{ width: "50%" }}>
                    <Typography
                      textAlign="left"
                      color={isDark ? "white" : "#00050b"}
                      sx={{ mb: 0.5 }}
                    >
                      CVV
                    </Typography>
                    <TextField
                      placeholder="123"
                      fullWidth
                      margin="dense"
                      sx={{ mb: 1 }}
                      autoComplete="off"
                      value={cvv}
                      onChange={(e) =>
                        setCvv(e.target.value.replace(/\D/g, "").slice(0, 3))
                      }
                      error={Boolean(errors.cvv)}
                      helperText={errors.cvv}
                      InputProps={{ sx: inputSx }}
                      inputProps={inputPadding}
                    />
                  </Box>
                </Box>

                {/* Name on Card */}
                <Typography
                  textAlign="left"
                  color={isDark ? "white" : "#00050b"}
                  sx={{ mt: 1, mb: 0.5 }}
                >
                  Name on Card
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter name as on card"
                  margin="dense"
                  sx={{ mb: 1 }}
                  autoComplete="off"
                  value={nameOnCard}
                  onChange={(e) =>
                    setNameOnCard(e.target.value.replace(/[^a-zA-Z\s]/g, ""))
                  }
                  error={Boolean(errors.nameOnCard)}
                  helperText={errors.nameOnCard}
                  InputProps={{ sx: inputSx }}
                  inputProps={inputPadding}
                />

                <Typography
                  variant="body2"
                  color="green"
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <LockIcon fontSize="small" sx={{ mr: 1 }} />
                  Secure Payment powered by TAPay
                </Typography>

                {/* ── Pay Button ── */}
                <Button
                  variant="contained"
                  fullWidth
                  disabled={isPaying}
                  onClick={handlePay}
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    height: 48,
                    borderRadius: "10px",
                    fontSize: 15,
                    bgcolor: isPaying ? "#166534" : "#16a34a",
                    "&:hover": { bgcolor: "#15803d" },
                    "&.Mui-disabled": { bgcolor: "#166534", color: "#fff" },
                    transition: "all 0.3s ease",
                  }}
                >
                  {isPaying ? (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <CircularProgress
                        size={18}
                        thickness={5}
                        sx={{ color: "#fff" }}
                      />
                      <Typography
                        sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}
                      >
                        We are navigating, please wait...
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LockIcon sx={{ fontSize: 18 }} />
                      Verify & Pay ₹{amount || ""}
                    </Box>
                  )}
                </Button>

                {/* ── Full-screen overlay ── */}
                {isPaying && (
                  <Box
                    sx={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 9999,
                      bgcolor: "rgba(0,0,0,0.55)",
                      backdropFilter: "blur(5px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: isDark ? "#0B1C30" : "#fff",
                        borderRadius: "20px",
                        px: 5,
                        py: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2.5,
                        boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
                        minWidth: 310,
                        maxWidth: 360,
                      }}
                    >
                      <Box sx={{ position: "relative", width: 72, height: 72 }}>
                        <CircularProgress
                          size={72}
                          thickness={3}
                          sx={{
                            color: "#22c55e",
                            position: "absolute",
                            top: 0,
                            left: 0,
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <LockIcon sx={{ fontSize: 30, color: "#22c55e" }} />
                        </Box>
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 17,
                          color: isDark ? "#fff" : "#0e1b2c",
                          textAlign: "center",
                        }}
                      >
                        We are navigating, please wait...
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 13,
                          color: isDark ? "#9bb3c9" : "#6b7280",
                          textAlign: "center",
                          lineHeight: 1.6,
                        }}
                      >
                        You are being redirected to the TAPay payment gateway.
                        <br />
                        Please do not press Back or Refresh.
                      </Typography>

                      <Box
                        sx={{
                          width: "100%",
                          height: 4,
                          borderRadius: 2,
                          bgcolor: isDark ? "#1e3a5f" : "#e5e7eb",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: "40%",
                            bgcolor: "#22c55e",
                            borderRadius: 2,
                            animation: "slide 1.2s ease-in-out infinite",
                            "@keyframes slide": {
                              "0%": { transform: "translateX(-100%)" },
                              "100%": { transform: "translateX(350%)" },
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default Loadusingccdc;
